import { uploadUrl } from "@/services/config";
import type { MediaLibraryItem } from "@/types";
import { dimensionLabel, fileKind, formatMediaDate, isImage } from "../utils";

interface MediaGridProps {
  items: MediaLibraryItem[];
  loading: boolean;
  error: string | null;
  view: "grid" | "list";
  activeId: string | null;
  selectedIds: Set<string>;
  onOpen: (id: string) => void;
  onToggleSelect: (id: string) => void;
  onRetry: () => void;
}

/** Small square checkbox used on each cell for bulk selection. */
function SelectBox({ checked, onToggle }: { checked: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-label={checked ? "Deselect" : "Select"}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={`flex h-5 w-5 items-center justify-center rounded border transition-colors cursor-pointer ${
        checked
          ? "border-primary-500 bg-primary-500 text-foreground-950"
          : "border-background-300 bg-background-50/90 text-transparent hover:border-primary-400"
      }`}
    >
      <i className="ri-check-line text-xs"></i>
    </button>
  );
}

/** Thumbnail tile: the file itself, or an icon when it can't be shown inline. */
function Thumb({ item, className = "" }: { item: MediaLibraryItem; className?: string }) {
  if (isImage(item)) {
    return <img src={uploadUrl(item.url)} alt={item.altText || item.title || item.originalName} className={`${className} object-cover`} />;
  }
  return (
    <div className={`${className} flex items-center justify-center bg-background-100 text-foreground-400`}>
      <i className="ri-file-line text-2xl"></i>
    </div>
  );
}

export default function MediaGrid({
  items,
  loading,
  error,
  view,
  activeId,
  selectedIds,
  onOpen,
  onToggleSelect,
  onRetry,
}: MediaGridProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="aspect-square animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-secondary-200 bg-secondary-50 px-6 py-14 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary-100 text-secondary-700">
          <i className="ri-wifi-off-line text-xl"></i>
        </span>
        <p className="mt-3 font-heading text-sm font-bold text-secondary-900">Couldn't load the media library</p>
        <p className="mt-1 text-xs text-secondary-700">{error}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-2 rounded-md border border-secondary-300 bg-background-50 px-4 py-2 text-sm font-semibold text-secondary-800 hover:bg-secondary-100 cursor-pointer"
        >
          <i className="ri-refresh-line"></i> Try again
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-background-100 text-foreground-500">
          <i className="ri-image-2-line text-2xl"></i>
        </span>
        <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">No media found</h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-600">
          Try a different search or upload new images with the Add New button.
        </p>
      </div>
    );
  }

  if (view === "list") {
    return (
      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        {items.map((item) => {
          const active = item.id === activeId;
          const checked = selectedIds.has(item.id);
          return (
            <div
              key={item.id}
              onClick={() => onOpen(item.id)}
              className={`flex cursor-pointer items-center gap-3 border-b border-background-100 px-3 py-2.5 last:border-b-0 ${
                active ? "bg-primary-50" : "hover:bg-background-100/60"
              }`}
            >
              <SelectBox checked={checked} onToggle={() => onToggleSelect(item.id)} />
              <Thumb item={item} className="h-11 w-11 shrink-0 rounded-md" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground-950">{item.title || item.originalName}</p>
                <p className="truncate text-xs text-foreground-500">{item.fileName}</p>
              </div>
              <span className="hidden text-xs text-foreground-500 md:block">{item.folder}</span>
              <span className="hidden text-xs text-foreground-500 lg:block">{dimensionLabel(item)}</span>
              <span className="hidden text-xs text-foreground-500 sm:block">{item.size}</span>
              <span className="hidden text-xs text-foreground-400 md:block">{formatMediaDate(item.createdAt)}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
      {items.map((item) => {
        const active = item.id === activeId;
        const checked = selectedIds.has(item.id);
        return (
          <figure
            key={item.id}
            onClick={() => onOpen(item.id)}
            className={`group cursor-pointer overflow-hidden rounded-lg border bg-background-50 transition-colors ${
              checked ? "border-primary-400" : active ? "border-primary-500" : "border-background-200 hover:border-primary-300"
            }`}
          >
            <div className="relative aspect-square w-full overflow-hidden">
              <Thumb item={item} className="h-full w-full transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute left-2 top-2">
                <SelectBox checked={checked} onToggle={() => onToggleSelect(item.id)} />
              </div>
              <span className="absolute right-2 top-2 inline-flex items-center rounded bg-background-950/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-background-50">
                {fileKind(item)}
              </span>
            </div>
            <figcaption className="px-2.5 py-2">
              <p className="truncate text-xs font-semibold text-foreground-900">{item.title || item.originalName}</p>
              <p className="truncate text-[11px] text-foreground-400">
                {item.folder} · {item.size}
              </p>
            </figcaption>
          </figure>
        );
      })}
    </div>
  );
}